import type { DiscountConfig, OfferSpecificSettings } from '@avalara/types/offers';
import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

import type { OfferState, OrderState } from '@avalara/types/reducer-states';

const initialState: OrderState = {
  discountAdditionalLegalText: null,
  discountCode: null,
  discountName: null,
  discountPercent: 0,
  hasPurchaseFailed: false,
  isConnectorValidated: false,
  isFetchingSalesTax: false,
  offerSpecificSettings: {},
  planSelectComplete: false,
  quantity: 1,
  salesTax: null,
  selectedPlanName: null,
};

type SelectPlanPayload = { planName: string | null; planSelectComplete: boolean };
type SetOrderQuantityPayload = { quantity: number; offer: OfferState };

const store = createSlice({
  name: 'order',
  initialState,
  reducers: {
    fetchingOrderSalesTax: (state, { payload }: PayloadAction<boolean>) => {
      state.isFetchingSalesTax = payload;
    },
    receiveOrderSalesTax: (state, { payload }: PayloadAction<number | null>) => {
      state.salesTax = payload;
    },
    selectPlan: (state, { payload }: PayloadAction<SelectPlanPayload>) => {
      state.selectedPlanName = payload.planName;
      state.planSelectComplete = payload.planSelectComplete;
    },
    setOrderQuantity: (state, { payload }: PayloadAction<SetOrderQuantityPayload>) => {
      const { offer, quantity } = payload;
      if (!offer.adjustableQuantity) return;

      const minQuantity = offer.minQuantity ?? 1;
      if (quantity < minQuantity) return;
      if (offer.maxQuantity != null && quantity > offer.maxQuantity) return;

      state.quantity = quantity;
      state.salesTax = null;
    },
    setConnectorValidationStatus: (state, { payload }: PayloadAction<boolean>) => {
      state.isConnectorValidated = payload;
    },
    setDiscountCode: (state, { payload }: PayloadAction<string | null>) => {
      state.discountCode = payload;
    },
    setDiscountPercent: (state, { payload }: PayloadAction<DiscountConfig>) => {
      state.discountPercent = payload.discountPercentage;
      state.discountAdditionalLegalText = payload.additionalLegalText;
      state.discountName = payload.discountName;
    },
    setOfferSpecificSettings: (state, { payload }: PayloadAction<OfferSpecificSettings>) => {
      state.offerSpecificSettings = { ...state.offerSpecificSettings, ...payload };
    },
    setPurchaseFailed: (state, { payload }: PayloadAction<boolean>) => {
      state.hasPurchaseFailed = payload;
    },
  },
});

export const {
  fetchingOrderSalesTax,
  receiveOrderSalesTax,
  selectPlan,
  setOrderQuantity,
  setConnectorValidationStatus,
  setDiscountCode,
  setDiscountPercent,
  setOfferSpecificSettings,
  setPurchaseFailed,
} = store.actions;

export default store;
